import { chakra, Icon, Table } from '@chakra-ui/react'
import { ArrowUp } from 'lucide-react'
import React from 'react'

export type SortDirection = 'asc' | 'desc'

type ColumnHeaderExtraProps = Omit<
  React.ComponentProps<typeof Table.ColumnHeader>,
  'aria-sort'
>

type SortButtonProps = Omit<
  React.ComponentProps<typeof chakra.button>,
  'type' | 'onClick' | 'children'
>

interface SortableColumnHeaderBaseProps {
  label: string
  /** API or display field this header sorts by. */
  field: string
  /**
   * Extra ColumnHeader props for sticky positioning, background, and similar.
   * `aria-sort` is owned by the component.
   */
  columnHeaderProps?: ColumnHeaderExtraProps
  /** Visual overrides for the sort button. Click handling stays on the component. */
  buttonProps?: SortButtonProps
}

export type OrderingModeProps = SortableColumnHeaderBaseProps & {
  /** Current DRF-style ordering: `field` (asc) or `-field` (desc). */
  ordering: string
  onOrderingChange: (value: string) => void
}

export type DirectionModeProps = SortableColumnHeaderBaseProps & {
  sortField: string | null
  direction: SortDirection | null
  onSortChange: (field: string, direction: SortDirection) => void
}

export type SortableColumnHeaderProps = OrderingModeProps | DirectionModeProps

const isDirectionMode = (
  props: SortableColumnHeaderProps,
): props is DirectionModeProps => 'onSortChange' in props

const mergeColumnHeaderCss = (
  iconOpacity: number,
  extra: ColumnHeaderExtraProps['css'],
) => {
  const iconRule = {
    '& .sortable-column-header-icon': { opacity: iconOpacity },
  }
  if (extra == null) return iconRule
  return [iconRule, ...(Array.isArray(extra) ? extra : [extra])]
}

export const SortableColumnHeader: React.FC<SortableColumnHeaderProps> = (
  props,
) => {
  const { label, field, columnHeaderProps, buttonProps } = props
  const isAsc = isDirectionMode(props)
    ? props.sortField === field && props.direction === 'asc'
    : props.ordering === field
  const isDesc = isDirectionMode(props)
    ? props.sortField === field && props.direction === 'desc'
    : props.ordering === `-${field}`
  const isActive = isAsc || isDesc

  const handleClick = () => {
    if (isDirectionMode(props)) {
      props.onSortChange(field, isAsc ? 'desc' : 'asc')
      return
    }
    props.onOrderingChange(isAsc ? `-${field}` : field)
  }

  const { css: extraCss, ...restColumnHeaderProps } = columnHeaderProps ?? {}

  return (
    <Table.ColumnHeader
      p={0}
      _hover={{ bg: 'bg.subtle' }}
      {...restColumnHeaderProps}
      css={mergeColumnHeaderCss(isActive ? 1 : 0.5, extraCss)}
      aria-sort={isAsc ? 'ascending' : isDesc ? 'descending' : 'none'}
    >
      {/* A real button so the sort is keyboard operable. */}
      <chakra.button
        type="button"
        onClick={handleClick}
        w="full"
        px={4}
        py={3}
        display="flex"
        alignItems="center"
        gap={1}
        fontWeight="medium"
        cursor="pointer"
        userSelect="none"
        borderRadius="sm"
        focusVisibleRing="outside"
        {...buttonProps}
      >
        {label}
        <Icon
          className="sortable-column-header-icon"
          boxSize={4}
          color={isActive ? 'fg.default' : 'fg.subtle'}
          transform={isDesc ? 'rotate(180deg)' : 'rotate(0deg)'}
          transition="transform 0.2s, opacity 0.2s"
        >
          <ArrowUp size="100%" />
        </Icon>
      </chakra.button>
    </Table.ColumnHeader>
  )
}
